<script setup lang="ts">
  // Period summary grid (§7 plan): one row per business day, stored day-close
  // aggregates when a snapshot exists, live computation otherwise. Row click
  // opens the day's Cierre del día page.
  import { computed, getCurrentInstance, ref } from 'vue';
  import DayClosesService from '@/api/services/day-closes.service';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsExportButton from '@/components/v2/stats/V2StatsExportButton.vue';
  import V2StatsTable from '@/components/v2/stats/V2StatsTable.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import { useUi } from '@/composition/use-ui';
  import {
    DayClosePaymentBucket,
    IDaySummaryRow,
  } from '@/interfaces/IDayClose';
  import { IStatsContext } from '@/interfaces/IStatsContext';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import { IStatsExportColumn } from '@/domain/stats/stats-export';
  import {
    IV2StatsTableColumn,
    IV2StatsTableRow,
  } from '@/components/v2/stats/v2-stats-table.types';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const ALL_BUCKETS: DayClosePaymentBucket[] = [
    'cash',
    'card',
    'online',
    'qr',
    'banktransfer',
    'club',
    'customer',
    'invitation',
    'employee',
  ];

  const rows = ref<IDaySummaryRow[]>([]);
  const loading = ref(false);
  const range = ref<IStatsDateRange | null>(null);
  const context = ref<IStatsContext | null>(null);

  function localToday(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  /** Branch business "today"; browser-local fallback if context fails. */
  const currentBusinessDate = computed(
    () => context.value?.currentBusinessDate ?? localToday()
  );

  function onContext(value: IStatsContext) {
    context.value = value;
  }

  // Guards out-of-order responses on rapid range/branch switches.
  let requestGeneration = 0;

  async function reload() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value) return;

    const generation = ++requestGeneration;
    loading.value = true;
    try {
      const result = await DayClosesService.getDaySummary({
        restaurantId: restaurant.id,
        branchId: branch.id,
        from: range.value.from,
        to: range.value.to,
      });
      if (generation !== requestGeneration) return;
      rows.value = result;
    } catch (error) {
      if (generation !== requestGeneration) return;
      ui.serverError(error, i18nT('v2Stats.daySummary.loadError'));
      rows.value = [];
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    reload();
  }

  //#region Formatting

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol || '$'
  );

  function money(value: unknown): string {
    if (value === null || typeof value === 'undefined') return '—';
    return `${currencySymbol.value}${displayPrice(Number(value), true)}`;
  }

  function integer(value: unknown): string {
    if (value === null || typeof value === 'undefined') return '—';
    return String(value);
  }

  function formatBusinessDate(value: string): string {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  function formatWindow(fromIso: string, toIso: string): string {
    const from = new Date(fromIso);
    const to = new Date(toIso);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return '';
    const fmt = (d: Date) =>
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${fmt(from)} – ${fmt(to)}`;
  }

  //#endregion

  //#region Table shape

  /** Buckets with movement in the period (screen shows only these). */
  const activeBuckets = computed<DayClosePaymentBucket[]>(() =>
    ALL_BUCKETS.filter((bucket) =>
      rows.value.some((row) => (row.tenders[bucket] ?? 0) !== 0)
    )
  );

  const tableRows = computed<IV2StatsTableRow[]>(() =>
    rows.value.map((row) => {
      const flat: IV2StatsTableRow = {
        id: row.businessDate,
        businessDate: row.businessDate,
        window: formatWindow(row.windowFrom, row.windowTo),
        windowFrom: row.windowFrom,
        windowTo: row.windowTo,
        // "Live" reads as "still in progress", so only the current business
        // day gets the badge; historical days without a snapshot (pre-epoch)
        // are final in practice and render with no badge. `>=` guards the
        // cutoff transition (rows refreshed after context was loaded).
        liveBadge: row.live && row.businessDate >= currentBusinessDate.value,
        seq: row.seq,
        amended: row.amended,
        stale: row.stale,
        mixedCurrencies: row.mixedCurrencies,
        orders: row.orders,
        covers: row.covers,
        // Loose ?? null: undefined when the admin ships before the API.
        spendPerCover: row.spendPerCover ?? null,
        grossSales: row.grossSales,
        benefitsTotal: row.benefitsTotal,
        rounding: row.rounding,
        netSales: row.netSales,
        taxes: row.taxes,
        dayTotal: row.dayTotal,
        tips: row.tips,
        nonSaleTotal: row.nonSaleTotal,
        collected: row.collected,
        // Loose ?? null: undefined when the admin ships before the API.
        collectedTotal: row.collectedTotal ?? null,
        overShort: row.overShort,
        avgTicket: row.avgTicket,
      };
      for (const bucket of ALL_BUCKETS) {
        flat[`tender_${bucket}`] = row.tenders[bucket] ?? 0;
      }
      return flat;
    })
  );

  // Importance-first order (USER-DECIDED): date/window/volume → money
  // headlines (bruto, neto, collections-based Cobrado) → tender split →
  // adjustments → derived ratios. The old kpis.collected column lives only
  // in the export ("Cobrado por ventas").
  const columns = computed<IV2StatsTableColumn[]>(() => [
    {
      key: 'businessDate',
      label: i18nT('v2Stats.daySummary.colDate'),
      format: (value) => formatBusinessDate(String(value)),
    },
    {
      key: 'window',
      label: i18nT('v2Stats.daySummary.colWindow'),
      sortable: false,
    },
    {
      key: 'orders',
      label: i18nT('v2Stats.daySummary.colOrders'),
      numeric: true,
      format: integer,
    },
    {
      key: 'covers',
      label: i18nT('v2Stats.daySummary.colCovers'),
      numeric: true,
      format: integer,
    },
    {
      key: 'grossSales',
      label: i18nT('v2Stats.daySummary.colGross'),
      numeric: true,
      format: money,
    },
    {
      key: 'netSales',
      label: i18nT('v2Stats.daySummary.colNet'),
      numeric: true,
      format: money,
    },
    {
      key: 'collectedTotal',
      label: i18nT('v2Stats.daySummary.colCollected'),
      numeric: true,
      format: money,
    },
    ...activeBuckets.value.map(
      (bucket): IV2StatsTableColumn => ({
        key: `tender_${bucket}`,
        label: i18nT(`v2Stats.bucket.${bucket}`),
        numeric: true,
        format: money,
      })
    ),
    {
      key: 'benefitsTotal',
      label: i18nT('v2Stats.daySummary.colDiscounts'),
      numeric: true,
      format: money,
    },
    {
      key: 'tips',
      label: i18nT('v2Stats.daySummary.colTips'),
      numeric: true,
      format: money,
    },
    {
      key: 'rounding',
      label: i18nT('v2Stats.daySummary.colRounding'),
      numeric: true,
      format: money,
    },
    {
      key: 'nonSaleTotal',
      label: i18nT('v2Stats.daySummary.colNonSale'),
      numeric: true,
      format: money,
    },
    {
      key: 'overShort',
      label: i18nT('v2Stats.daySummary.colOverShort'),
      numeric: true,
      format: money,
    },
    {
      key: 'avgTicket',
      label: i18nT('v2Stats.daySummary.colAvgTicket'),
      numeric: true,
      format: money,
    },
    {
      key: 'spendPerCover',
      label: i18nT('v2Stats.daySummary.colSpendPerCover'),
      numeric: true,
      format: money,
    },
  ]);

  const totals = computed<Record<string, unknown> | null>(() => {
    if (!rows.value.length) return null;

    const sum = (pick: (row: IDaySummaryRow) => number) =>
      rows.value.reduce((acc, row) => acc + pick(row), 0);
    const sumNullable = (pick: (row: IDaySummaryRow) => number | null) => {
      const present = rows.value
        .map(pick)
        .filter((v): v is number => v !== null);
      return present.length
        ? present.reduce((acc, v) => acc + v, 0)
        : null;
    };

    const totalOrders = sum((row) => row.orders);
    const totalNet = sum((row) => row.netSales);

    // Covers-weighted spend-per-cover average (dashboard precedent). LOOSE
    // != null on purpose: rows are undefined when the admin ships before
    // the API, and a strict filter would produce NaN.
    let spendPerCoverRevenue = 0;
    let spendPerCoverCovers = 0;
    for (const row of rows.value) {
      if (row.covers != null && row.spendPerCover != null) {
        spendPerCoverRevenue += row.spendPerCover * row.covers;
        spendPerCoverCovers += row.covers;
      }
    }

    const result: Record<string, unknown> = {
      businessDate: null,
      window: null,
      orders: totalOrders,
      covers: sumNullable((row) => row.covers),
      spendPerCover:
        spendPerCoverCovers > 0
          ? spendPerCoverRevenue / spendPerCoverCovers
          : null,
      grossSales: sum((row) => row.grossSales),
      benefitsTotal: sum((row) => row.benefitsTotal),
      rounding: sum((row) => row.rounding),
      netSales: totalNet,
      taxes: sum((row) => row.taxes),
      dayTotal: sum((row) => row.dayTotal),
      tips: sum((row) => row.tips),
      nonSaleTotal: sum((row) => row.nonSaleTotal),
      collectedTotal: sumNullable((row) => row.collectedTotal ?? null),
      overShort: sumNullable((row) => row.overShort),
      avgTicket: totalOrders > 0 ? totalNet / totalOrders : null,
    };
    for (const bucket of ALL_BUCKETS) {
      result[`tender_${bucket}`] = sum((row) => row.tenders[bucket] ?? 0);
    }
    return result;
  });

  function openDayClose(row: IV2StatsTableRow) {
    const restaurant = restaurantStore.restaurant;
    if (!restaurant) return;
    proxy.$router.push(
      `/${restaurant.stringId}/stats/day-close?date=${row.businessDate}`
    );
  }

  //#endregion

  //#region Export (always ALL columns, including hidden tender buckets)

  const exportColumns = computed<IStatsExportColumn[]>(() => [
    { key: 'businessDate', header: i18nT('v2Stats.daySummary.colDate') },
    { key: 'windowFrom', header: i18nT('v2Stats.daySummary.colWindowFrom') },
    { key: 'windowTo', header: i18nT('v2Stats.daySummary.colWindowTo') },
    { key: 'seq', header: i18nT('v2Stats.daySummary.colSeq'), type: 'number' },
    {
      key: 'orders',
      header: i18nT('v2Stats.daySummary.colOrders'),
      type: 'number',
    },
    {
      key: 'covers',
      header: i18nT('v2Stats.daySummary.colCovers'),
      type: 'number',
    },
    {
      key: 'grossSales',
      header: i18nT('v2Stats.daySummary.colGross'),
      type: 'number',
    },
    {
      key: 'netSales',
      header: i18nT('v2Stats.daySummary.colNet'),
      type: 'number',
    },
    {
      key: 'collectedTotal',
      header: i18nT('v2Stats.daySummary.colCollected'),
      type: 'number',
    },
    ...ALL_BUCKETS.map(
      (bucket): IStatsExportColumn => ({
        key: `tender_${bucket}`,
        header: i18nT(`v2Stats.bucket.${bucket}`),
        type: 'number',
      })
    ),
    // Order-attributed tenders (old kpis.collected); export-only, with a
    // header distinct from the collections-based "Cobrado" column.
    {
      key: 'collected',
      header: i18nT('v2Stats.dayClose.collectedSales'),
      type: 'number',
    },
    {
      key: 'benefitsTotal',
      header: i18nT('v2Stats.daySummary.colDiscounts'),
      type: 'number',
    },
    {
      key: 'tips',
      header: i18nT('v2Stats.daySummary.colTips'),
      type: 'number',
    },
    {
      key: 'rounding',
      header: i18nT('v2Stats.daySummary.colRounding'),
      type: 'number',
    },
    {
      key: 'taxes',
      header: i18nT('v2Stats.daySummary.colTaxes'),
      type: 'number',
    },
    {
      key: 'dayTotal',
      header: i18nT('v2Stats.daySummary.colDayTotal'),
      type: 'number',
    },
    {
      key: 'nonSaleTotal',
      header: i18nT('v2Stats.daySummary.colNonSale'),
      type: 'number',
    },
    {
      key: 'overShort',
      header: i18nT('v2Stats.daySummary.colOverShort'),
      type: 'number',
    },
    {
      key: 'avgTicket',
      header: i18nT('v2Stats.daySummary.colAvgTicket'),
      type: 'number',
    },
    {
      key: 'spendPerCover',
      header: i18nT('v2Stats.daySummary.colSpendPerCover'),
      type: 'number',
    },
  ]);

  //#endregion
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.daySummary.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="day-summary"
        business-only
        @change="onRangeChange"
        @context="onContext"
      />
    </template>

    <template #actions>
      <V2StatsExportButton
        :filename="String($t('v2Stats.daySummary.title'))"
        :sheet-name="String($t('v2Stats.daySummary.title'))"
        :columns="exportColumns"
        :rows="tableRows"
        :disabled="loading"
      />
    </template>

    <V2StatsTable
      :columns="columns"
      :rows="tableRows"
      :totals="totals"
      :loading="loading"
      :default-sort="{ key: 'businessDate', desc: true }"
      clickable-rows
      sticky-first
      :empty-text="String($t('v2Stats.daySummary.empty'))"
      @row-click="openDayClose"
    >
      <template #cell-businessDate="{ row }">
        <span class="day-date">{{
          formatBusinessDate(String(row.businessDate))
        }}</span>
        <PosUiBadge v-if="row.seq" class="day-badge" color="neutral" size="xs">
          Z{{ row.seq }}
        </PosUiBadge>
        <PosUiBadge
          v-if="row.liveBadge"
          class="day-badge"
          color="info"
          size="xs"
        >
          {{ $t('v2Stats.dayClose.liveBadge') }}
        </PosUiBadge>
        <PosUiBadge
          v-if="row.amended"
          class="day-badge"
          color="warning"
          size="xs"
        >
          {{ $t('v2Stats.dayClose.amendedBadge') }}
        </PosUiBadge>
        <PosUiBadge
          v-if="row.stale"
          class="day-badge"
          color="warning"
          size="xs"
        >
          {{ $t('v2Stats.dayClose.staleBadge') }}
        </PosUiBadge>
      </template>
    </V2StatsTable>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .day-date {
    @apply tw-font-medium tw-text-neutral-900;
  }

  .day-badge {
    @apply tw-ml-1.5;
  }
</style>
